import React, { useEffect, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SbCurrentUser, KitDocumentType } from '../submission_boards/component/SbModel';
import { HmuKitDocumentModel, KitDocumentModel } from '../submission_kits/components/KitModels';
import MediaKitModal from '../submission_boards/component/MediaKitModal';
import FilePreviewer from '../submission_kits/components/FilePreviewer';
import hmuKitService from '../services/HmuKitService';
import mediaKitService from '../services/MediaKitService';
import { showToast } from '../shared/Toast';
import { CircleMinusIcon, XIcon } from 'lucide-react';

export interface HmuKitPhotosProps {
  currentUser: SbCurrentUser;
  maxPhotos?: number;
}

export default function HmuKitPhotos({ currentUser, maxPhotos = 4 }: HmuKitPhotosProps) {
  const [hmuKitDocuments, setHmuKitDocuments] = useState<HmuKitDocumentModel[]>([]);
  const [mediaKitDocuments, setMediaKitDocuments] = useState<KitDocumentModel[]>([]);
  const [showMediaKitModal, setShowMediaKitModal] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [featured, setFeatured] = useState(0);

  useEffect(() => {
    fetchHmuKitDocuments();
    fetchMediaKits();
  }, []);

  const featureImageClickHandler = async (index: number, hmuKitDoc: HmuKitDocumentModel, featured: boolean) => {
    try {
      setIsLoading(true);
      const response = await hmuKitService.markKitAsFeatured(hmuKitDoc.hair_make_up_kit_id, hmuKitDoc.kit_document.id, featured);
      if (response) {

      }
    } catch (error) {
      showToast('Error fetching Hair & Make-up Kit photos.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHmuKitDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await hmuKitService.getHmuKitDocuments();
      if (response.status === 'success') {
        response.hmu_kit_documents.map((item) => {
          if (item.tag && item.tag == '1') {
            setFeatured(item.kit_document.id);
          }
        })
        setHmuKitDocuments(response.hmu_kit_documents || []);
      }
    } catch (error) {
      showToast('Error fetching Hair & Make-up Kit photos.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMediaKits = async () => {
    try {
      const responseData = await mediaKitService.fetchMediaKits('', 'new', 1, 5);
      if (responseData && responseData.kit_documents) {
        setMediaKitDocuments(responseData.kit_documents);
      }
    } catch (error) {
      console.error('Error fetching media kits:', error);
    }
  };

  const handleCardClick = (index: number) => {
    setSelectedCardIndex(index);
    setShowMediaKitModal(true);
  };

  const handleMediaSelected = async (kitDocument: KitDocumentModel): Promise<boolean> => {
    if (!kitDocument || !kitDocument.id) {
      showToast('Please select a media file.', 'danger');
      return false;
    }

    try {
      const tag = (selectedCardIndex != null && selectedCardIndex == 0) ? '1' : '';
      await hmuKitService.addInHmuKitDocument(kitDocument.id, tag);
      await fetchHmuKitDocuments();
      setSelectedCardIndex(null);
      showToast('Media added successfully to Hair & Make-up Kit.', 'success');
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error adding media to Hair & Make-up Kit.';
      showToast(errorMessage, 'danger');
      return false;
    }
  };

  const handleRemove = async (hmuKitDocument: HmuKitDocumentModel) => {
    try {
      await hmuKitService.deleteFromHmuKitDocument(hmuKitDocument);
      await fetchHmuKitDocuments();
      showToast('Media removed successfully.', 'success');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error removing media.';
      showToast(errorMessage, 'danger');
    }
  };

  const renderCard = (index: number) => {
    // First slot (index 0) shows only featured image (tag='1')
    // Other slots show non-featured images (tag != '1') in order
    let mainKitDoc;
    let aspectRatio = 4 / 5;
    if (index === 0) {
      // aspectRatio = 4/5;
      mainKitDoc = hmuKitDocuments.find(doc => doc.tag === '1');
    } else {
      // Get all non-featured documents and pick by adjusted index
      const nonFeaturedDocs = hmuKitDocuments.filter(doc => doc.tag !== '1');
      mainKitDoc = nonFeaturedDocs[index - 1];
    }
    const isEmpty = !mainKitDoc;

    return (<div className={`flex flex-col gap-2-1 bg-white rounded-xl overflow-hidden  ${isEmpty ? ' ' : ''}`}
      style={{
        aspectRatio: aspectRatio,
        // maxHeight: index > 0 ? '13.072rem' : '26.643rem',
        // minHeight: index > 0 ? '13.072rem' : '26.643rem',
        // width: index > 0 ? '9.813rem' : 'auto',
        // maxWidth: index > 0 ? 'auto' : '20rem',
        // height: index > 0 ? '13.072rem' : '26.643rem'
      }}>
      <div
        key={index}
        className={`flex flex-col gap-2 bg-blue-201 rounded-2xl hover:shadow-ring-0-0-0-2-4 cursor-pointer ${isEmpty ? 'h-full' : ''} 
        ${index === 0 ? 'hmu-featured-card' : ''}`}
        onClick={() => {
          if (isEmpty) {
            handleCardClick(index);
          }
        }}
        style={{
          // minHeight: '12.5rem'
        }}
      >
        {isEmpty ? (
          <div
            className="flex flex-col items-center justify-center h-full bg-blue-50 text-gray-400 rounded-2xl"
            style={{
              backgroundColor: 'rgba(60, 130, 246, 0.05)',
              border: '1.5px dashed rgb(60, 130, 246)',
            }}
          >

            <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="text-sm"></span>
            {index == 0 && <span className="text-sm">Featured Image</span>}
          </div>
        ) : (
          <div className="relative flex-1">
            <FilePreviewer
              file_url={mainKitDoc.kit_document.cropped_file_url || mainKitDoc.kit_document.file_url}
              file_name={mainKitDoc.kit_document.file_name}
              file_content_type={mainKitDoc.kit_document.file_content_type}
              showControl={false}
              dynamicHeight={true}
              object_fit='object-cover'
            />
            <button
              id={'image_id_' + index}
              type="button"
              className="absolute flex items-center justify-center h-6 w-6 top-1 right-1 rounded-full shadow-md hover:scale-110 transition-transform text-white hover:text-black bg-opacity-40 bg-black hover:bg-white"
              style={{
                // backgroundColor: 'rgba(0, 0, 0, 0.3)',
              }}
              onClick={async (e) => {
                e.stopPropagation();
                handleRemove(mainKitDoc);
              }}
            >
              <div className="h-5 w-5">
                <CircleMinusIcon className="h-5 w-5" />
              </div>
            </button>

          </div>
        )}
      </div>
      {false && !isEmpty && <div className='flex gap-2-1 justify-center items-center'>
        <input
          id={`featured_` + index}
          name={`featured`}
          type="checkbox"
          // checked={selectedUserGroups.length<=0?false: (selectedUserGroups.filter((item) => item <= 0).length > 0?true:false)}
          checked={featured == mainKitDoc.kit_document.id}
          className="rounded-full bg-white shadow-sm border border-gray-300  border-0 focus:shadow-outline text-left cursor-pointer"
          onChange={async function (selectedValue: any) {
            if (featured == mainKitDoc.kit_document.id) {
              setFeatured(0);
              await featureImageClickHandler(index, mainKitDoc, false);

            } else {
              setFeatured(mainKitDoc.kit_document.id);
              await featureImageClickHandler(index, mainKitDoc, true);
            }
          }}
        /> Featured image
      </div>}
    </div>
    );
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-1-1" style={{
      }}>
        {/* first photo */}
        <div className="grid grid-cols-1 gap-2" style={{
          // minHeight: '26.643rem',
          // maxHeight: '26.643rem',
        }}>
          {Array.from({ length: maxPhotos }).map((_, index) => {
            if (index > 0) {
              return <></>;
            }
            return renderCard(index);
          })}
        </div>
        <div className="grid grid-cols-2 gap-1-1"
        >
          {Array.from({ length: maxPhotos }).map((_, index) => {
            if (index == 0) {
              return <></>;
            }
            return renderCard(index);

          })}
        </div>
      </div>

      {showMediaKitModal && (
        <MediaKitModal
          currentUser={currentUser}
          from="hmu_kit"
          mediaKitDocuments={mediaKitDocuments}
          hmuKitDocuments={hmuKitDocuments}
          filetype={KitDocumentType.Image}
          dialogCloseHandler={() => {
            setShowMediaKitModal(false);
          }}
          selectedMediaKitHandler={handleMediaSelected}
        />
      )}
    </>
  );
}

